// ── Login Page ──
//
// Panes off local `mode`: signin (email+password + Google), signup (the
// verified-email wizard, C1496), twofa (6-digit code, second step of a 2FA
// login). `tempToken` is held in a module-local var only — never localStorage,
// it's a 5-minute-TTL token the server hard-rejects on any protected route
// (C1128). The wizard's `signupToken` follows the identical rule — see its
// declaration below.
//
// Desktop sign-in handoff (C1249): the Task App (Electron) opens this page in
// the system browser at `#/login?d=<base64url {port,nonce}>` instead of going
// straight to Google itself. `d` is decoded into module-local `desktopHandoff`
// and mirrored to sessionStorage (so a reload mid-flow — e.g. during the 2FA
// step or the signup wizard — doesn't strand the user back on a plain sign-in
// screen). Any successful sign-in/sign-up/2FA in that mode redirects the
// browser to the Task App's loopback callback
// (`http://127.0.0.1:<port>/callback?token=…`) instead of navigating into the
// SPA. The Google branch carries `d` through as `?desktop=` on
// `/api/auth/google/login`, which threads it through Google's OAuth `state`
// and back out the other side — see `api/src/routes/auth.js`'s
// `parseDesktopState()`/`desktop` branch.

import { verifySession, loginWithPassword, completeTwoFactorLogin, signupStart, signupVerify, signupComplete } from '../auth.js';
import { getToken, apiFetch } from '../api.js';
import { navigate } from '../router.js';
import { teardownLayout } from '../layout.js';
import { showToast, esc } from '../ui.js';
import { renderAvatarHtml } from '../utils.js';
import { EXPERIENCE_OPTIONS, signupErrorText, passwordToggleAttrs } from '../lib/signup-form.js';
import { bindAvatarPicker } from '../lib/avatar-pick.js';

const HANDOFF_STORAGE_KEY = 'tt_desktop_handoff';

// Feather-style eye / eye-off. aria-hidden — the button (not the icon) carries
// the accessible label.
const EYE_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

let mode = 'signin';
let tempToken = null;
let pendingEmail = '';
let desktopHandoff = null;

// ── Signup wizard state (C1496) ──
// signupStep: which of the wizard's three sub-panes to show while
// mode === 'signup'. signupStep only ever advances to 'profile' via a
// successful signupVerify() call (see bind()'s #signup-code-form handler) —
// that assignment, not a UI check, is what makes "profile is unreachable
// until the email is verified" true.
let signupStep = 'email';   // 'email' | 'code' | 'profile'
let signupEmail = '';
// The signup token is a stateless 30-minute JWT ({email, signup_pending:true})
// that requireAuth hard-rejects on every protected route — same shape and
// same rule as `tempToken` above (the 2FA temp_token): it authenticates
// nothing on its own, so it must live in memory only, NEVER
// localStorage/sessionStorage. Unlike `desktopHandoff` below (which is
// deliberately mirrored to sessionStorage so a reload survives), a reload
// mid-wizard is meant to drop back to a fresh signin pane.
let signupToken = null;
let signupAvatar = null;    // data: URL from fileToAvatarDataUrl(), or null
let resendReadyAt = 0;      // epoch ms the Resend button re-enables (0 = never sent)
let resendTimerId = null;

// ── Beta registration handoff (TPT96) ──
// betaCode: threaded through signupStart()/signupComplete() so the server
// can re-check + eventually consume the beta_signups row it came from.
// Blank is fine — it just means "no code, let the server's invite/beta-mode
// checks decide" (an invited user, or beta mode off).
// pendingBeta: set by pages/register.js's startBetaSignup() export before it
// navigate()s here — applied once, in loginPage()'s tail below, then
// cleared. Module state survives the hash navigation because it's the same
// JS context (no full page reload), so there is nothing to persist to
// sessionStorage for this handoff, unlike invite.js's PENDING_INVITE_KEY.
let betaCode = '';
let pendingBeta = null;

function resetSignupState() {
  signupStep = 'email';
  signupEmail = '';
  signupToken = null;
  signupAvatar = null;
  betaCode = '';
  resendReadyAt = 0;
  stopResendTimer();
}

// Called by pages/register.js once it has validated a beta code against
// POST /auth/beta/validate. Queues the wizard to open pre-seeded with the
// discovered email and code the next time loginPage() runs — the caller
// navigates('/login') right after this.
export function startBetaSignup({ code, email }) {
  pendingBeta = { code, email };
}

function stopResendTimer() {
  if (resendTimerId) { clearInterval(resendTimerId); resendTimerId = null; }
}

// Ticks the Resend button's countdown. Self-healing: router.js never invokes
// a page's cleanup return value (see Gotchas in tt-web-auth.md), so if the
// user has navigated away from the code pane while this interval is still
// running, `#signup-resend-btn` is simply gone from the DOM — treat that as
// the signal to stop, rather than relying on every navigation path to
// remember to call stopResendTimer() itself.
function updateResendButton() {
  const btn = document.getElementById('signup-resend-btn');
  if (!btn) { stopResendTimer(); return; }
  const secondsLeft = Math.ceil((resendReadyAt - Date.now()) / 1000);
  if (secondsLeft > 0) {
    btn.disabled = true;
    btn.textContent = `Resend code (${secondsLeft}s)`;
  } else {
    btn.disabled = false;
    btn.textContent = 'Resend code';
    stopResendTimer();
  }
}

function startResendTimer() {
  stopResendTimer();
  updateResendButton();
  resendTimerId = setInterval(updateResendButton, 1000);
}

// ── Desktop handoff helpers ──

function decodeHandoff(d) {
  try {
    const b64 = d.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const obj = JSON.parse(atob(b64 + pad));
    if (
      Number.isInteger(obj.port) && obj.port >= 1024 && obj.port <= 65535 &&
      typeof obj.nonce === 'string' && /^[a-f0-9]{1,64}$/i.test(obj.nonce)
    ) {
      return { port: obj.port, nonce: obj.nonce };
    }
  } catch { /* malformed — treat as no handoff */ }
  return null;
}

function encodeHandoff(handoff) {
  return btoa(JSON.stringify(handoff)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function readHandoffFromHash() {
  const hash = window.location.hash || '';
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return null;
  const d = new URLSearchParams(hash.slice(qIdx + 1)).get('d');
  return d ? decodeHandoff(d) : null;
}

function readStoredHandoff() {
  try {
    const raw = sessionStorage.getItem(HANDOFF_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Redirect the browser to the Task App's loopback callback with the freshly
// issued token. Called instead of navigate('/') on any successful sign-in/up
// (including the signup wizard's final step) while desktopHandoff is set.
// `explicitToken` — the cookie→Bearer bridge token from GET /auth/session-token
// (C1466) — is only passed by the cookie-only-session shortcut in loginPage();
// every other caller omits it and falls back to getToken() (already
// populated via setAuthToken()). This is a full-page navigation out of the
// SPA — nothing may run after calling it.
function completeHandoff(explicitToken) {
  const { port, nonce } = desktopHandoff;
  sessionStorage.removeItem(HANDOFF_STORAGE_KEY);
  desktopHandoff = null;
  const token = explicitToken || getToken();
  window.location.href =
    `http://127.0.0.1:${port}/callback?token=${encodeURIComponent(token)}&nonce=${encodeURIComponent(nonce)}`;
}

function render() {
  const app = document.getElementById('app');
  app.innerHTML = `
    <div class="login-container">
      <h1>Tip<span class="brand-delta">Δ</span>Task</h1>
      ${mode === 'signin' ? renderSignin() : mode === 'signup' ? renderSignupWizard() : renderTwofa()}
    </div>`;
  bind();
}

function renderSignin() {
  const googleHref = '/api/auth/google/login' + (desktopHandoff ? ('?desktop=' + encodeHandoff(desktopHandoff)) : '');
  return `
    ${desktopHandoff ? '<p>Signing in to the TipΔTask desktop app</p>' : ''}
    <form class="login-form" id="signin-form">
      <div class="form-group">
        <label class="form-label" for="signin-email">Email</label>
        <input class="form-input" id="signin-email" type="email" autocomplete="email" required>
      </div>
      <div class="form-group">
        <label class="form-label" for="signin-password">Password</label>
        <input class="form-input" id="signin-password" type="password" autocomplete="current-password" required>
      </div>
      <div class="login-error" id="signin-error"></div>
      <button type="submit" class="btn btn-primary" id="signin-btn" style="width:100%;">Sign In</button>
    </form>
    <div class="login-divider"><span>or</span></div>
    <a href="${googleHref}" class="btn btn-ghost login-google-btn">Sign in with Google</a>
    <div class="login-switch">No account? <a href="#" id="go-signup">Create account</a></div>`;
}

// ── Signup wizard panes (C1496) ──
// One dispatcher (the name the task requires) + three step renderers, mirroring
// the signin/signup/twofa dispatch in render() above. Only renderSignupWizard()
// is called from outside this block.
function renderSignupWizard() {
  const handoffNotice = desktopHandoff ? '<p>Signing in to the TipΔTask desktop app</p>' : '';
  if (signupStep === 'code') return handoffNotice + renderSignupCodeStep();
  if (signupStep === 'profile') return handoffNotice + renderSignupProfileStep();
  return handoffNotice + renderSignupEmailStep();
}

function renderSignupEmailStep() {
  // TPT96 — betaCode set means this step was reached via a validated
  // registration link (pages/register.js), not the plain "Create account"
  // link — the email input below is still editable/submittable exactly the
  // same either way, this is purely a confirmation line.
  // TPT103 — signupEmail is deliberately blank for a shared (team) code
  // (register.js skips passing the original applicant's email) — showing
  // "confirmed for <the applicant's address>" to a teammate would leak a
  // stranger's email, so the notice only renders when there's a real,
  // specific address to confirm.
  const betaNotice = (betaCode && signupEmail)
    ? `<p>Beta access confirmed for <strong>${esc(signupEmail)}</strong></p>`
    : '';
  return `
    ${betaNotice}
    <p>Create your account</p>
    <form class="login-form" id="signup-email-form">
      <div class="form-group">
        <label class="form-label" for="signup-email">Email</label>
        <input class="form-input" id="signup-email" type="email" autocomplete="email" required autofocus>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary" id="signup-send-btn" style="width:100%;">Send code</button>
    </form>
    <div class="login-switch">Already have an account? <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderSignupCodeStep() {
  return `
    <p>Step 2 of 3 — enter the 6-digit code sent to <strong>${esc(signupEmail)}</strong></p>
    <form class="login-form" id="signup-code-form">
      <div class="form-group">
        <label class="form-label" for="signup-code">Verification code</label>
        <input class="form-input" id="signup-code" inputmode="numeric" autocomplete="one-time-code" autofocus required>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary" id="signup-verify-btn" style="width:100%;">Verify</button>
      <button type="button" class="btn btn-ghost btn-sm" id="signup-resend-btn" style="width:100%;margin-top:0.5rem;">Resend code</button>
    </form>
    <div class="login-switch"><a href="#" id="signup-change-email">Use a different email</a> · <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderSignupProfileStep() {
  const avatarHtml = renderAvatarHtml(signupAvatar ? { avatar_url: signupAvatar } : null, 96);
  const experienceOptions = EXPERIENCE_OPTIONS
    .map(o => `<option value="${o.value}">${esc(o.label)}</option>`)
    .join('');
  return `
    <p>Step 3 of 3 — complete your profile</p>
    <form class="login-form" id="signup-profile-form">
      <input type="file" id="signup-avatar-input" accept="image/*" hidden>
      <div class="form-group" style="text-align:center;">
        <span id="signup-avatar-preview" class="avatar-drop-zone" style="display:inline-block;cursor:pointer;" title="Add a photo">${avatarHtml}</span>
        <div>
          <button type="button" class="btn btn-ghost btn-sm" id="signup-avatar-remove" style="margin-top:0.5rem;${signupAvatar ? '' : 'display:none;'}">Remove photo</button>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-verified-email">Email</label>
        <input class="form-input" id="signup-verified-email" type="email" autocomplete="username" readonly>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-name">Name (optional)</label>
        <input class="form-input" id="signup-name" autocomplete="name">
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-password">Password</label>
        <div class="password-field has-toggle">
          <input class="form-input" id="signup-password" type="password" autocomplete="new-password" minlength="8" required>
          <button type="button" class="password-toggle" id="signup-password-toggle" aria-label="Show password" aria-pressed="false" title="Show password">${EYE_ICON}</button>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-confirm">Confirm password</label>
        <div class="password-field">
          <input class="form-input" id="signup-confirm" type="password" autocomplete="new-password" minlength="8" required>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-headline">Headline (optional)</label>
        <input class="form-input" id="signup-headline" maxlength="160" placeholder="e.g. Senior backend engineer">
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-background">Background (optional)</label>
        <textarea class="form-textarea" id="signup-background" maxlength="4000"></textarea>
      </div>
      <div class="form-group">
        <label class="form-label" for="signup-experience">Experience (optional)</label>
        <select class="form-select" id="signup-experience">${experienceOptions}</select>
      </div>
      <div class="login-error" id="signup-error"></div>
      <button type="submit" class="btn btn-primary" id="signup-complete-btn" style="width:100%;">Create account</button>
    </form>
    <div class="login-switch">Already have an account? <a href="#" id="go-signin">Sign in</a></div>`;
}

function renderTwofa() {
  return `
    <p>Enter the 6-digit code from your authenticator app, or a backup code${pendingEmail ? ` for <strong>${esc(pendingEmail)}</strong>` : ''}</p>
    <form class="login-form" id="twofa-form">
      <div class="form-group">
        <label class="form-label" for="twofa-code">Code or backup code</label>
        <input class="form-input" id="twofa-code" autocomplete="one-time-code" autofocus required>
      </div>
      <div class="login-error" id="twofa-error"></div>
      <button type="submit" class="btn btn-primary" id="twofa-btn" style="width:100%;">Verify</button>
    </form>
    <div class="login-switch"><a href="#" id="go-back">Back</a></div>`;
}

// Shared eye toggle for the profile step's password + confirm inputs — a
// confirm field only earns its keep if the two typed values can be compared
// side by side, so one click reveals/masks both together and both
// aria-pressed states stay in sync (only the password field's button exists
// in the DOM; `scope` restricts the query to this form so the toggle can
// never reach #signin-password).
//
// State lives ONLY in the DOM (`input.type`), never in a module-local
// variable: render() replaces #app's innerHTML wholesale on every step change
// or wizard reset, rebuilding this pane from a template that hardcodes
// type="password"/aria-pressed="false" — that's what makes "step 3 always
// comes back masked" true with no reset code to remember. A signupAvatar-
// style flag would survive the re-render and silently re-reveal the fields,
// which is exactly the bug this avoids.
function togglePasswordVisibility(scope) {
  const btn = scope.querySelector('.password-toggle');
  const revealed = btn.getAttribute('aria-pressed') !== 'true';
  const { type, label, pressed } = passwordToggleAttrs(revealed);
  for (const input of scope.querySelectorAll('.password-field .form-input')) {
    // Assigning .type resets the caret to the end in some engines; both
    // 'password' and 'text' support the selection API, so save/restore it.
    const { selectionStart, selectionEnd } = input;
    input.type = type;
    if (document.activeElement === input && selectionStart !== null) {
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  }
  btn.setAttribute('aria-pressed', pressed);
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.innerHTML = revealed ? EYE_OFF_ICON : EYE_ICON;
}

function bind() {
  const app = document.getElementById('app');

  const goSignup = app.querySelector('#go-signup');
  if (goSignup) goSignup.addEventListener('click', (e) => { e.preventDefault(); resetSignupState(); mode = 'signup'; render(); });

  const goSignin = app.querySelector('#go-signin');
  if (goSignin) goSignin.addEventListener('click', (e) => { e.preventDefault(); resetSignupState(); mode = 'signin'; render(); });

  const goBack = app.querySelector('#go-back');
  if (goBack) goBack.addEventListener('click', (e) => { e.preventDefault(); tempToken = null; mode = 'signin'; render(); });

  const signinForm = app.querySelector('#signin-form');
  if (signinForm) {
    signinForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signin-btn');
      const errEl = app.querySelector('#signin-error');
      const email = app.querySelector('#signin-email').value.trim();
      const password = app.querySelector('#signin-password').value;
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Signing in...';
      try {
        const result = await loginWithPassword(email, password);
        if (result.requires2fa) {
          tempToken = result.tempToken;
          pendingEmail = email;
          mode = 'twofa';
          render();
          return;
        }
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        errEl.textContent = err.message || 'Invalid email or password';
        btn.disabled = false;
        btn.textContent = 'Sign In';
      }
    });
  }

  // ── Signup wizard bindings (C1496) ──

  const signupEmailForm = app.querySelector('#signup-email-form');
  if (signupEmailForm) {
    const emailInput = app.querySelector('#signup-email');
    emailInput.value = signupEmail;
    signupEmailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-send-btn');
      const errEl = app.querySelector('#signup-error');
      // Normalize the same way the server does (auth.js lowercases+trims) so
      // the code pane's displayed address and the resend cooldown key match
      // exactly what was actually sent to.
      const email = emailInput.value.trim().toLowerCase();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Sending...';
      try {
        await signupStart(email, betaCode);
        signupEmail = email;
        resendReadyAt = Date.now() + 60_000;
        signupStep = 'code';
        render();
      } catch (err) {
        errEl.textContent = signupErrorText(err, 'start');
        btn.disabled = false;
        btn.textContent = 'Send code';
      }
    });
  }

  const signupCodeForm = app.querySelector('#signup-code-form');
  if (signupCodeForm) {
    signupCodeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-verify-btn');
      const errEl = app.querySelector('#signup-error');
      const code = app.querySelector('#signup-code').value.trim();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Verifying...';
      try {
        const result = await signupVerify(signupEmail, code);
        // This assignment is the only path to signupStep = 'profile' — the
        // structural guarantee that step 2 is unreachable without a verified
        // code, not just a UI convention.
        signupToken = result.signupToken;
        stopResendTimer();
        signupStep = 'profile';
        render();
      } catch (err) {
        errEl.textContent = signupErrorText(err, 'verify');
        btn.disabled = false;
        btn.textContent = 'Verify';
      }
    });

    // type="button" is load-bearing here — this sits inside <form> and would
    // otherwise submit it (and thus verify with an empty code) on click.
    const resendBtn = app.querySelector('#signup-resend-btn');
    resendBtn.addEventListener('click', async () => {
      const errEl = app.querySelector('#signup-error');
      errEl.textContent = '';
      resendBtn.disabled = true;
      try {
        await signupStart(signupEmail, betaCode);
        resendReadyAt = Date.now() + 60_000;
        startResendTimer();
        showToast('New code sent', 'success');
      } catch (err) {
        // A cooldown 429 here still means "wait ~60s", so reset the same
        // countdown rather than leaving the button permanently disabled with
        // no way to know when it'll re-enable.
        resendReadyAt = Date.now() + 60_000;
        startResendTimer();
        errEl.textContent = signupErrorText(err, 'start');
      }
    });
    startResendTimer(); // resumes the countdown across re-renders of this pane
  }

  const changeEmail = app.querySelector('#signup-change-email');
  if (changeEmail) {
    changeEmail.addEventListener('click', (e) => {
      e.preventDefault();
      // The code row is consumed at verify — there is no going "back" to a
      // still-valid code, only starting over with a (possibly new) address.
      signupToken = null;
      stopResendTimer();
      signupStep = 'email';
      render();
    });
  }

  const signupProfileForm = app.querySelector('#signup-profile-form');
  if (signupProfileForm) {
    const verifiedEmailInput = app.querySelector('#signup-verified-email');
    verifiedEmailInput.value = signupEmail;

    const avatarInput = app.querySelector('#signup-avatar-input');
    const avatarPreview = app.querySelector('#signup-avatar-preview');
    const avatarRemoveBtn = app.querySelector('#signup-avatar-remove');

    bindAvatarPicker({
      preview: avatarPreview,
      input: avatarInput,
      removeBtn: avatarRemoveBtn,
      form: signupProfileForm,
      onPick: (dataUrl) => {
        signupAvatar = dataUrl;
        avatarPreview.innerHTML = renderAvatarHtml({ avatar_url: signupAvatar }, 96);
      },
      onRemove: () => {
        signupAvatar = null;
        avatarPreview.innerHTML = renderAvatarHtml(null, 96);
      },
      onError: (msg) => { app.querySelector('#signup-error').textContent = msg; },
    });

    const pwToggle = signupProfileForm.querySelector('.password-toggle');
    if (pwToggle) {
      // Decline focus on mousedown so the caret stays in whatever field is
      // being typed in — without this the button takes focus and the
      // selection restore in togglePasswordVisibility() never has anything
      // to restore.
      pwToggle.addEventListener('mousedown', (e) => e.preventDefault());
      pwToggle.addEventListener('click', () => togglePasswordVisibility(signupProfileForm));
    }

    signupProfileForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#signup-complete-btn');
      const errEl = app.querySelector('#signup-error');
      const name = app.querySelector('#signup-name').value;
      const password = app.querySelector('#signup-password').value;
      const confirm = app.querySelector('#signup-confirm').value;
      const headline = app.querySelector('#signup-headline').value;
      const background = app.querySelector('#signup-background').value;
      const experienceYears = app.querySelector('#signup-experience').value;
      errEl.textContent = '';
      if (password !== confirm) {
        errEl.textContent = 'Passwords do not match';
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Creating account...';
      try {
        // buildSignupBody() (called inside signupComplete, lib/signup-form.js)
        // trims strings and omits blank optionals — raw values are fine here.
        await signupComplete(signupToken, {
          name, password, avatar: signupAvatar, headline, background,
          experience_years: experienceYears,
        }, betaCode);
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        if (err.status === 401) {
          // Signup token expired/invalid (30 min TTL, or already spent) — the
          // only fix is to prove the email again. Toast + full restart rather
          // than an inline message, since nothing on this pane can resolve it.
          showToast('Your verification expired — please start again', 'error');
          resetSignupState();
          render();
          return;
        }
        errEl.textContent = signupErrorText(err, 'complete');
        btn.disabled = false;
        btn.textContent = 'Create account';
      }
    });
  }

  const twofaForm = app.querySelector('#twofa-form');
  if (twofaForm) {
    twofaForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = app.querySelector('#twofa-btn');
      const errEl = app.querySelector('#twofa-error');
      const code = app.querySelector('#twofa-code').value.trim();
      errEl.textContent = '';
      btn.disabled = true;
      btn.textContent = 'Verifying...';
      try {
        const result = await completeTwoFactorLogin(tempToken, code);
        tempToken = null;
        if (result.backupCodeUsed) {
          showToast(`Signed in with a backup code — ${result.backupCodesRemaining} left`, 'success');
        }
        if (desktopHandoff) { completeHandoff(); return; }
        navigate('/');
      } catch (err) {
        if (err.status === 401 && /expired|session/i.test(err.message || '')) {
          showToast('Session expired — sign in again', 'error');
          tempToken = null;
          mode = 'signin';
          render();
          return;
        }
        errEl.textContent = err.message || 'Invalid code';
        btn.disabled = false;
        btn.textContent = 'Verify';
      }
    });
  }
}

export async function loginPage() {
  // Pick up a desktop handoff either fresh off the URL (the Task App just
  // opened this page) or from a prior page load in the same tab (e.g. the
  // user reloaded mid-flow) — see module comment above.
  const fromHash = readHandoffFromHash();
  if (fromHash) {
    desktopHandoff = fromHash;
    try { sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(fromHash)); } catch { /* ignore */ }
  } else {
    desktopHandoff = readStoredHandoff();
  }

  // Landing here can mean two things: a genuinely logged-out visitor, or a
  // browser that just returned from the Google OAuth redirect (JWT already
  // set as an httpOnly cookie, no manual refresh should be needed). Re-check
  // the session silently before painting the login form so the latter case
  // bounces straight into the app.
  if (await verifySession()) {
    if (!desktopHandoff) { navigate('/'); return; }

    // Handoff needs a portable bearer token for the loopback callback. A
    // password/2FA/signup-signed-in browser already has one in localStorage
    // (getToken()); a cookie-only Google session doesn't, since the cookie
    // alone drives the SPA (C1249). For that case, mint one from the live
    // cookie session (GET /auth/session-token, C1466) instead of falling
    // through to the sign-in form — `_silent` since a mint failure here is
    // not a "session expired" event, just "fall back to the form" (see
    // `tt-web-auth.md` § Gotchas). The Google button itself never reaches
    // this branch — it's routed straight to the loopback server-side
    // (`desktop` branch in api/src/routes/auth.js).
    let token = getToken();
    if (!token) {
      try {
        const res = await apiFetch('/auth/session-token', { _silent: true });
        token = res.token;
      } catch { /* fall through to the sign-in form below */ }
    }
    if (token) { completeHandoff(token); return; }
  }

  teardownLayout();
  mode = 'signin';
  tempToken = null;
  pendingEmail = '';
  resetSignupState();

  // TPT96 — a validated beta code from pages/register.js takes us straight
  // into the signup wizard's email step, pre-filled. renderSignupEmailStep()'s
  // existing `emailInput.value = signupEmail` line needs no change to pick
  // this up.
  if (pendingBeta) {
    betaCode = pendingBeta.code;
    signupEmail = pendingBeta.email;
    mode = 'signup';
    signupStep = 'email';
    pendingBeta = null;
  }

  render();
}
